/**
 * Hooks React Query pour la persistance XP (MVP2.5 T5).
 *
 * Backend prérequis (live en prod depuis PR #69 vague 1) :
 *   - POST /api/v1/training/xp  body {module, source_ref, xp_amount}
 *       → 201 {awarded, total_today, total_alltime}
 *   - GET  /api/v1/training/xp/daily?days=N
 *       → {days_window, entries:[{date, total}, ...]}
 *   - GET  /api/v1/training/xp/cumul
 *       → {total_alltime, total_30d, streak_days}
 *
 * Spec : docs/WAKEUP_MVP2.5.md §2 T5.
 *
 * Fallback silencieux : si l'endpoint répond 401 (non authentifié) ou 404
 * (pas encore migré sur cet hôte), on resolve avec un état "no-op" plutôt
 * que de remonter une erreur. L'objectif (T5 §"backward-compat") est que
 * l'UI ne crash jamais à cause de la couche XP — qui est un bonus, pas
 * une fonctionnalité critique du drill.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { http, type ApiError } from './http';

// ─── Types réponse backend (cf. XpService.php) ─────────────────────────────

export type XpModule = 'opening' | 'tactic' | 'endgame' | 'autopsy' | 'mission_brief';

export interface XpAwardPayload {
  module: XpModule;
  source_ref: string;
  xp_amount: number;
}

export interface XpAwardResponse {
  awarded: boolean;
  total_today: number;
  total_alltime: number;
}

export interface XpDailyEntry {
  date: string;
  total: number;
}

export interface XpDailyResponse {
  days_window: number;
  entries: XpDailyEntry[];
}

export interface XpCumulResponse {
  total_alltime: number;
  total_30d: number;
  streak_days: number;
}

export interface XpDisplay {
  today: number;
  alltime: number;
  streak: number;
}

// ─── Helpers ────────────────────────────────────────────────────────────────

/**
 * Vrai si l'erreur lève venait d'un 401 (pas authentifié) ou 404 (endpoint
 * absent — utile pendant la migration de prod). Tous les autres cas (500,
 * réseau down, etc.) restent visibles via React Query et seront loggués au
 * call-site si besoin.
 */
function isSilentFallback(err: unknown): err is ApiError {
  if (typeof err !== 'object' || err === null) return false;
  const status = (err as ApiError).status;
  return status === 401 || status === 404;
}

// ─── useAwardXp — mutation ──────────────────────────────────────────────────

/**
 * Mutation POST /api/v1/training/xp.
 *
 * En succès : invalide les queries 'xp' pour rafraîchir le display Mission
 * Control. En 401/404 : retourne `{awarded:false, total_today:0,
 * total_alltime:0}` sans throw — le drill continue normalement.
 */
export function useAwardXp() {
  const qc = useQueryClient();
  return useMutation<XpAwardResponse, ApiError, XpAwardPayload>({
    mutationFn: async (payload) => {
      try {
        return await http.post<XpAwardResponse>('/api/v1/training/xp', payload);
      } catch (err) {
        if (isSilentFallback(err)) {
          return { awarded: false, total_today: 0, total_alltime: 0 };
        }
        throw err;
      }
    },
    onSuccess: (data) => {
      if (data.awarded) {
        void qc.invalidateQueries({ queryKey: ['xp'] });
      }
    },
  });
}

// ─── useTodayXp — XP du jour ───────────────────────────────────────────────

/**
 * Renvoie le XP gagné aujourd'hui. On consomme `daily?days=1` qui retourne
 * une liste d'1 entrée pour le jour courant (cf. XpService::getDailyXp).
 *
 * Le service garantit qu'on a toujours `days` entrées contiguës, donc
 * `entries[0]` existe — mais on prend défensivement la dernière entrée
 * (la plus récente) pour rester robuste au cas où le shape évolue.
 */
export function useTodayXp() {
  return useQuery<number, ApiError>({
    queryKey: ['xp', 'today'],
    queryFn: async () => {
      try {
        const res = await http.get<XpDailyResponse>('/api/v1/training/xp/daily?days=1');
        const last = res.entries[res.entries.length - 1];
        return last?.total ?? 0;
      } catch (err) {
        if (isSilentFallback(err)) return 0;
        throw err;
      }
    },
    staleTime: 30_000,
    retry: 0,
  });
}

// ─── useCumulXp — totals ────────────────────────────────────────────────────

/**
 * Renvoie le cumul (all-time + 30j + streak). Cache 30s : Mission Control
 * affiche ces valeurs en footer, pas besoin de temps réel.
 */
export function useCumulXp() {
  return useQuery<XpCumulResponse, ApiError>({
    queryKey: ['xp', 'cumul'],
    queryFn: async () => {
      try {
        return await http.get<XpCumulResponse>('/api/v1/training/xp/cumul');
      } catch (err) {
        if (isSilentFallback(err)) {
          return { total_alltime: 0, total_30d: 0, streak_days: 0 };
        }
        throw err;
      }
    },
    staleTime: 30_000,
    retry: 0,
  });
}

// ─── useXpDisplay — agrégat pour Mission Control footer ────────────────────

/**
 * Hook composite : combine useTodayXp + useCumulXp pour le footer Mission
 * Control. Retourne un objet stable même si les deux sous-queries sont
 * en pending — le caller affiche 0 / 0 / 0 pendant le premier load.
 */
export function useXpDisplay(): XpDisplay {
  const today = useTodayXp();
  const cumul = useCumulXp();
  return {
    today: today.data ?? 0,
    alltime: cumul.data?.total_alltime ?? 0,
    streak: cumul.data?.streak_days ?? 0,
  };
}
